import {DatePipe} from '@angular/common';
import {Website} from '../club/website';
import {TixxSelectStatus} from './tixx-select-status';
import {LotteryStatusType} from '../../enum';
import {LotteryStatusTranslationService} from 'src/app/shared/translations';
import {DateObject} from '../index';

/**
 * Blueprint for the tixx event data
 */
export class TixxEvent {
  public id: number;
  public name: string;
  public competitionName: string;
  public date: DateObject;
  public homePerformer: string;
  public homePerformerLogoUrl: string;
  public guestPerformer: string;
  public guestPerformerLogoUrl: string;
  public location: string;
  public summary: string;

  constructor(event: any, private datePipe: DatePipe) {
    this.id = event.id;
    this.name = event.name;
    this.competitionName = event.competitionName;
    this.date = event.date ? new DateObject(event.date, 'dd.MM.yyyy HH:mm') : null;
    this.homePerformer = event.homePerformer;
    this.homePerformerLogoUrl = event.homePerformerLogoUrl;
    this.guestPerformer = event.guestPerformer;
    this.guestPerformerLogoUrl = event.guestPerformerLogoUrl;
    this.location = event.location;
    this.summary = this.setSummary(event);
  }

  /**
   * Set the summary
   */
  setSummary(event: any): string {
    const date = this.datePipe.transform(event.date, 'dd.MM.yyyy HH:mm');
    return `${event.id} | ${date} ${event.name}`;
  }
}

/**
 * Blueprint for the tixx event type data
 */
 export class TixxEventType {
  public id: number;
  public name: string;
  public organizer: string;
  public aggregateIdentifier: number;
  public eventTypeId: string;

  constructor(type: any) {
    this.id = type.id;
    this.name = type.name;
    this.organizer = type.organizer;
    this.aggregateIdentifier = type.aggregateIdentifier;
    this.eventTypeId = `/api/lottery-event-types/${type.id}`;
  }
}


/**
 * Blueprint for the lottery event data
 */
export class LotteryEvent {
  public id: number;
  public tixxId: number;
  public name: string;
  public competition: string;
  public homeTeam: string;
  public homeTeamLogoUrl: string;
  public guestTeam: string;
  public guestTeamLogoUrl: string;
  public heroImage: string;
  public date: DateObject;
  public flexibleDate: boolean;
  public venue: string;
  public guidelineText: string;
  public guidelineHeadline: string;
  public specialRequirementText: string;
  public specialRequirementDescription: string;
  public type: any;

  constructor(event: any) {
    this.id = event.id;
    this.tixxId = event.tixxId;
    this.name = event.name;
    this.competition = event.competition;
    this.homeTeam = event.homeTeam;
    this.homeTeamLogoUrl = event.homeTeamLogoUrl;
    this.guestTeam = event.guestTeam;
    this.guestTeamLogoUrl = event.guestTeamLogoUrl;
    this.heroImage = event.heroImage;
    this.flexibleDate = event.flexibleDate;
    this.date = event.date ? new DateObject(event.date, 'dd.MM.yyyy HH:mm') : null;
    this.venue = event.venue;
    this.guidelineText = event.guidelineText;
    this.guidelineHeadline = event.guidelineHeadline;
    this.specialRequirementText = event.specialRequirementText;
    this.specialRequirementDescription = event.specialRequirementDescription;
    this.type = event.type;
  }

  public getFormattedEventName(): string {
    if (!this.homeTeam && !this.guestTeam) {
      return this.name;
    }

    let formattedEventName: string = this.name;

    if (this.homeTeam) {
      formattedEventName = `${formattedEventName} ${this.homeTeam}`;
    }

    if (this.homeTeam && this.guestTeam) {
      formattedEventName = `${formattedEventName} - `;
    }

    if (this.guestTeam) {
      formattedEventName = `${formattedEventName}${this.guestTeam}`;
    }

    return formattedEventName;
  }
}

/**
 * Blueprint for the lottery data
 */
export class Lottery {
  public id: number;
  public '@id': string;
  public executionId: number;
  public organizer: string;
  public submissionSetup: any;
  public event: LotteryEvent;
  public website: Website;
  public status: TixxSelectStatus;

  constructor(lottery: any, t: LotteryStatusTranslationService) {
    this.id = lottery.id;
    this.executionId = lottery.executionId;
    this['@id'] = lottery['@id'];
    this.organizer = lottery.organizer;
    this.submissionSetup = lottery.submissionSetup;
    this.event = new LotteryEvent(lottery.event);
    this.website = new Website(lottery.website);
    this.status = new TixxSelectStatus(lottery.status, t);
  }

  get isDraftMode(): boolean {
    return this.status.value === LotteryStatusType.DRAFT;
  }

  get isEditMode(): boolean {
    return this.status.value === LotteryStatusType.EDIT;
  }

  get isActivated(): boolean {
    return this.status.value === LotteryStatusType.ACTIVATED;
  }

  get isInternalMode(): boolean {
    return this.status.value === LotteryStatusType.INTERNAL;
  }

  get isExecutionMode(): boolean {
    return this.status.value === LotteryStatusType.EXECUTION;
  }

  get isCompleted(): boolean {
    return this.status.value === LotteryStatusType.COMPLETED;
  }
}

export class Organizer {
  public '@id': string;
  public logo: string;

  constructor(organizer: any) {
    this['@id'] = organizer['@id'];
    this.logo = organizer.logo;
  }
}
